import { useEffect, useRef, useState } from "react";

import { useValueDebounce } from "hooks/useValueDebounce";

import { getDetectorNameValidationError } from "./utils";

type UseIsDetectorNameValidReturn = {
  isValidating: boolean;
  error: string;
};

export function useIsDetectorNameValid(
  detectorName: string,
  detectorGroupId?: string,
  enabled: boolean = true
): UseIsDetectorNameValidReturn {
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState("");
  const debouncedDetectorName = useValueDebounce(detectorName);
  const detectorNameRef = useRef("");

  useEffect(() => {
    detectorNameRef.current = detectorName;
  }, [detectorName]);

  useEffect(() => {
    let isMounted = true;
    const setErrorIfMounted = (val: string) => {
      if (isMounted) setError(val);
    };
    const setIsValidatingIfMounted = (val: boolean) => {
      if (isMounted) setIsValidating(val);
    };

    setErrorIfMounted("");

    if (!enabled) {
      setIsValidatingIfMounted(false);
      return () => {
        isMounted = false;
      };
    }

    setIsValidatingIfMounted(true);

    const validateAsync = async () => {
      if (detectorNameRef.current !== debouncedDetectorName) {
        return;
      }

      const error = await getDetectorNameValidationError(debouncedDetectorName, detectorGroupId);

      if (isMounted && detectorNameRef.current === debouncedDetectorName) {
        if (error !== "") {
          setErrorIfMounted(error);
        }
        setIsValidatingIfMounted(false);
      }
    };

    validateAsync();

    return () => {
      isMounted = false;
    };
  }, [debouncedDetectorName, detectorGroupId, enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    setIsValidating(true);
  }, [detectorName, enabled]);

  return {
    error,
    isValidating,
  };
}
